import React, { useMemo, useState, useRef, useEffect } from 'react';
import { usePost } from '../API';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { useLocalizedStrings } from '../Localization';
import { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent, Card, CardBody, CardHeader, Button, Badge, DataTable, Loading, Page, Heading, Tooltip, TextArea, Select, Combobox } from '@aidkitorg/component-library';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { TagBadge } from './TagsUtil';

// Main Component fetches orgs.
const UserManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [startOpen, setStartOpen] = useState(false);
  const getOrgs = usePost('/organization/list');
  // TODO: integrate with new tag system
  const getEditableTags = usePost('/tags/list');
  const [editableTags, setEditableTags] = useState<string[]>([]);

  useAsyncEffect(async () => {
    const tags = await getEditableTags({});
    if (Array.isArray(tags)) {
      setEditableTags(tags);
    }
  }, []);

  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    const orgs = await getOrgs({});
    if (Array.isArray(orgs)) {
      setOrgs(orgs);
      if (orgs.length === 1) {
        setStartOpen(true);
      }
    }
    setLoading(false);
  }, []);

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <Page
      header={
        <div>
          <Heading level="h2">{L.user.user_management}</Heading>
          <p>{L.user.user_management_description}</p>
        </div>
      }
      children={
        <Accordion type="multiple" value={startOpen ? orgs.map(org => org.uid) : undefined} className='mt-10' >
          {orgs.map(org => (
            <AccordionItem value={org.uid} key={org.uid}>
              <Card >
                <AccordionTrigger >
                  {org.name}
                </AccordionTrigger>
                <AccordionContent>
                  <OrganizationBody org={org} editableTags={editableTags} />
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
      }
    />
  );
};

function OrganizationBody({ org, editableTags }: { org: Organization, editableTags: string[]   }) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const getGroups = usePost('/user-group/list');
  const L = useLocalizedStrings();

  useAsyncEffect(async () => {
    setLoading(true);
    const userGroups = await getGroups({ organization: org.uid });
    if (Array.isArray(userGroups)) {
      setGroups(userGroups);
    }
    setLoading(false);
  }, [org.uid]);

  const addNewGroup = () => {
    setGroups([
      {
        id: 0,
        name: '',
        organization: org.uid,
        permissions: '',
        metadata: {},
      },
      ...groups,
    ]);
  };

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <div>
      <Button variant="secondary" onClick={addNewGroup}>{L.user.add_group}</Button>
      {groups.map(group => (
        <UserGroupCard key={group.id} group={group} setGroups={setGroups} groups={groups} editableTags={editableTags} />
      ))}
    </div>
  );
}

function UserGroupCard({ group, setGroups, groups, editableTags }: { group: UserGroup, setGroups: (groups: UserGroup[]) => void, groups: UserGroup[], editableTags: string[] }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const getUsers = usePost('/user/list');
  const L = useLocalizedStrings();
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<User>>({});
  const updateUser = usePost('/user/update');
  const updateUserGroup = usePost('/user-group/update');
  const createUserGroup = usePost('/user-group/create');
  const createUser = usePost('/user/create');
  const deleteUser = usePost('/user/delete');
  const deleteUserGroup = usePost('/user-group/delete');
  const [editingGroup, setEditingGroup] = useState(group.id === 0); // On new group creation id is 0
  const editUserValuesRef = useRef<Partial<User>>({});
  const editGroupValuesRef = useRef<UserGroup>(group);
  const [canEditGroup, setCanEditGroup] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<{ label: string, value: string }[]>(
    group.permissions
      ? group.permissions.split(',').map(p => ({ label: p, value: p }))
      : []
  );

  useEffect(() => {
    const hasAdmin = editableTags.includes('admin');
    const groupPerms = group.permissions.split(',').map(p => p.trim());
    const hasAllPerms = groupPerms.every(perm => editableTags.includes(perm));
    const canEdit = hasAdmin || hasAllPerms;
    setCanEditGroup(canEdit);
    console.log({ editableTags, groupPerms, hasAdmin, hasAllPerms, canEdit });
  }, [editableTags, group.permissions]);

  useAsyncEffect(async () => {
    setLoading(true);
    const users = await getUsers({ organization: group.organization, user_group: group.id });
    if (Array.isArray(users)) {
      setUsers(users);
    }
    setLoading(false);
  }, [group.id, group.organization]);

  const columns = useMemo(() => [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }: any) => {
        const orig = row.original as User;
        if (editingUid === orig.uid) {
          return (
            <EditableCell
              initialValue={draft.name ?? orig.name}
              onValueChange={val => { editUserValuesRef.current.name = val; }}
            />
          );
        }
        return orig.name;
      },
    },
    {
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }: any) =>
        editingUid === row.original.uid ? (
          <EditableCell
            initialValue={draft.email ?? ''}
            onValueChange={val => { editUserValuesRef.current.email = val; }}
          />
        ) : (
          row.original.email
        ),
    },
    {
      header: 'Phone',
      accessorKey: 'phone',
      cell: ({ row }: any) =>
        editingUid === row.original.uid ? (
          <EditableCell
            initialValue={draft.phone ?? ''}
            onValueChange={val => { editUserValuesRef.current.phone = val; }}
          />
        ) : (
          row.original.phone
        ),
    },
    {
      header: 'Actions',
      cell: ({ row }: any) => (
        editingUid === row.original.uid ? (
          <>
            <Button onClick={saveUserEdit} size="sm" variant="primary">{L.applicant.notes.save}</Button>
            <Button onClick={cancelUserEdit} size="sm" variant="secondary" className="ml-2">{L.applicant.notes.cancel}</Button>
            <Button
              onClick={async () => {
                if (window.confirm(L.user.confirm_delete_user)) {
                  await deleteUser({ uid: row.original.uid });
                  setUsers(users.filter(u => u.uid !== row.original.uid));
                  setEditingUid(null);
                }
              }}
              size="sm"
              variant="danger"
              className="ml-2"
            >
              {L.user.delete_user}
            </Button>
          </>
        ) : (
          ((row.original.source !== 'User Management') || !canEditGroup) ?
            <Tooltip text={!canEditGroup ? L.user.edit_group_disabled : L.user.edit_user_disabled} asChild={true}>
              <Button 
                onClick={() => {}} 
                size="sm" 
                variant="secondary" 
                isDisabled={true}
              >{L.user.edit_user}</Button>
            </Tooltip>
            : 
            <Button 
              onClick={() => startUserEdit(row.original)} 
              size="sm" 
              variant="secondary" 
            >{L.user.edit_user}</Button>
        )
      )
    }
  ], [editingUid, draft, setDraft, canEditGroup]);

  const startUserEdit = (user: User) => {
    setEditingUid(user.uid);
    setDraft({ ...user });
  };

  const cancelUserEdit = () => {
    // If we are editing a new user, we need to remove it from the list
    if (editingUid === '0') {
      setUsers(users.filter(u => u.uid !== '0'));
    }
    setEditingUid(null);
    setDraft({});
    editUserValuesRef.current = {};
  };

  const saveUserEdit = async () => {
    if (editingUid) {
      const updatedUser = { uid: editingUid, user_group: group.id, ...editUserValuesRef.current };
      let updateResult: User;
      if (editingUid === '0') {
        updateResult = await createUser({
          name: updatedUser.name ?? '',
          phone: updatedUser.phone ?? '',
          email: updatedUser.email ?? '',
          user_group: group.id,
          metadata: updatedUser.metadata ?? '',
          organization: group.organization,
        });
      } else {
        updateResult = await updateUser(updatedUser);
      }
      if (updateResult?.uid) {
        if (editingUid === '0') {
          setUsers([updateResult, ...users.filter(u => u.uid !== '0')]);
        } else {
          setUsers(users.map(u => u.uid === updateResult.uid ? updateResult : u));
        }
      }
    }
    setEditingUid(null);
    editUserValuesRef.current = {};
  };

  const doDeleteUser = async (user: User) => {
    await deleteUser({ uid: user.uid });
    setUsers(users.filter(u => u.uid !== user.uid));
  };

  const doDeleteUserGroup = async (group: UserGroup) => { 
    await deleteUserGroup({ id: group.id });
    setGroups(groups.filter(g => g.id !== group.id));
  };

  const startGroupEdit = () => {
    setEditingGroup(true);
  };

  const cancelGroupEdit = () => {
    if (group.id === 0) {
      // If we are editing a new group, we need to remove it from the list
      setGroups(groups.filter(g => g.id !== 0));
    }
    setEditingGroup(false);
  };

  const saveGroupEdit = async () => {
    if (editingGroup) {
      let updateResult: UserGroup;
      if (group.id === 0) {
        const {id, ...createParams} = editGroupValuesRef.current;
        updateResult = await createUserGroup(createParams);
      } else {
        updateResult = await updateUserGroup(editGroupValuesRef.current);
      }
      if (updateResult?.id) {
        if (group.id === 0) {
          setGroups([updateResult, ...groups.filter(g => g.id !== 0)]); 
        } else {
          setGroups(groups.map(g => g.id === updateResult.id ? updateResult : g));
        }
      }
    }
    setEditingGroup(false);
  };

  const addNewUser = () => {
    setUsers([
      {
        uid: '0',
        name: '',
        email: '',
        phone: '',
        user_group: group.id,
        organization: group.organization,
        permissions: group.permissions,
        metadata: '',
        password: '',
        source: 'User Management',
      },
      ...users
    ]);
    setEditingUid('0');
  };

  const groupEditButtons = editingGroup ? 
    <>
      <Button isDisabled={!canEditGroup} onClick={saveGroupEdit} size="md" variant="primary">{L.user.save}</Button>
      <Button isDisabled={!canEditGroup} onClick={cancelGroupEdit} size="md" variant="secondary" className="ml-2">{L.user.cancel}</Button>
      <Button
        isDisabled={!canEditGroup}
        onClick={async () => {
          if (window.confirm(L.user.confirm_delete_group)) {
            await deleteUserGroup({ id: group.id });
            setGroups(groups.filter(g => g.id !== group.id));
          }
        }}
        size="md"
        variant="danger"
        className="ml-2"
      >
        {L.user.delete_group}
      </Button>
    </>
    :
    <>
      <Button isDisabled={!canEditGroup} onClick={addNewUser} size="md" variant="secondary">{L.user.add_user}</Button>
      <Button isDisabled={!canEditGroup} onClick={startGroupEdit}  size="md" variant="secondary" className="ml-2">{L.user.edit_group}</Button>
    </>;

  if (loading) return <Loading labels={[{ content: L.user.fetching_users }]} />;

  return (
    <Card className="mt-2">
      <CardHeader 
        title={group.name} 
        actionButton={canEditGroup ? 
          groupEditButtons
          : <Tooltip text={L.user.edit_group_disabled} >{groupEditButtons}</Tooltip>
        } />
      <CardBody>
        { editingGroup ? (
          <form
            onSubmit={e => {
              e.preventDefault();
              saveGroupEdit();
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium mb-1">{L.user.name}</label>
              <input
                className="border rounded px-2 py-1 w-full"
                defaultValue={group.name}
                onChange={e => { editGroupValuesRef.current.name = e.target.value; }}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium mb-1">{L.user.permissions}</label>
              <div className="flex flex-wrap gap-2">
                {editableTags.map(tag => {
                  const selected = selectedPermissions.some(p => p.value === tag);
                  return (
                    <label key={tag} className={`cursor-pointer ${selected ? 'bg-blue-100' : 'bg-gray-100'} rounded px-2 py-1 flex items-center gap-1`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          let newSelected;
                          if (selected) {
                            newSelected = selectedPermissions.filter(p => p.value !== tag);
                          } else {
                            newSelected = [...selectedPermissions, { label: tag, value: tag }];
                          }
                          setSelectedPermissions(newSelected);
                          editGroupValuesRef.current.permissions = newSelected.map(v => v.value).join(',');
                        }}
                        className="mr-1"
                      />
                      <span >
                        <TagBadge tag={tag} />
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </form>
        ) : (
          <div className="mb-2">
            {group.permissions?.length > 0 && group.permissions.split(',').map(permission => 
              <TagBadge key={permission} tag={permission} />
            )}
          </div>
        )}
        {users.length ? 
          <DataTable
            columns={columns}
            data={users}
            getRowId={(row: User, index: number) => {
              return row.uid ?? String(index);
            }}
            pageSize={100}
          /> : 
        // No users and editing means we are adding a new group, we don't want to show the no users message yet
          editingGroup ? 
            <></> : 
            <div className="text-center text-sm text-gray-500 mt-10 mb-10">{L.user.no_users_in_group}</div>
        }
      </CardBody>
    </Card>
  );
}

type EditableCellProps = {
  initialValue: string;
  onValueChange: (value: string) => void;
};

const EditableCell: React.FC<EditableCellProps> = ({ initialValue, onValueChange }) => {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    onValueChange(value);
  }, [value, onValueChange]);

  return (
    <input
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={() => onValueChange(value)}
      onKeyDown={e => {
        if (e.key === "Enter") {
          onValueChange(value);
        }
      }}
      className="border rounded px-2 py-1 w-full"
    />
  );
};

export default function UserManagementPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (Array.isArray(result?.navigation)) {
      setNav(result.navigation);
    }
  }, []);
  
  return (
    <ThreeColumnPage
      nav={nav}
      title="User Management"
      main={<UserManagement />}
    />
  );
}
